import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Trophy,
  Target,
  TrendingUp,
  Calendar,
  Users,
  Beaker,
  FileText,
  ChevronUp,
  ChevronDown,
  Minus,
  Award,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component
function StatCard({ title, value, subtitle, icon: Icon, trend, color = 'sky' }) {
  const colorClasses = {
    sky: 'bg-sky-500/10 border-sky-500/20 text-sky-400',
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    amber: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
    purple: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  };

  return (
    <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-6 h-6" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${trend > 0 ? 'text-emerald-400' : trend < 0 ? 'text-red-400' : 'text-zinc-500'}`}>
            {trend > 0 ? <ChevronUp className="w-3 h-3" /> : trend < 0 ? <ChevronDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <h3 className="text-3xl font-light text-white font-['Outfit']">{value}</h3>
      <p className="text-sm text-zinc-400 font-['Manrope'] mt-1">{title}</p>
      {subtitle && <p className="text-xs text-zinc-500 mt-2">{subtitle}</p>}
    </div>
  );
}

// Progress Ring Component
function ProgressRing({ progress, size = 120, color = 'sky' }) {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  const colorMap = {
    sky: '#0ea5e9',
    emerald: '#10b981',
    amber: '#f59e0b',
    purple: '#a855f7',
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255,255,255,0.05)"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={colorMap[color]}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center flex-col">
        <span className="text-2xl font-light text-white font-['Outfit']">{progress}%</span>
      </div>
    </div>
  );
}

// Ranking Item Component
function RankingItem({ rank, name, value, isCurrentUser }) {
  const rankColors = {
    1: 'text-amber-400',
    2: 'text-zinc-300',
    3: 'text-amber-700',
  };

  return (
    <div className={`flex items-center gap-4 p-3 rounded-xl ${isCurrentUser ? 'bg-sky-500/10 border border-sky-500/20' : 'hover:bg-white/[0.02]'}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${rank <= 3 ? 'bg-white/5' : ''}`}>
        {rank <= 3 ? (
          <Trophy className={`w-4 h-4 ${rankColors[rank]}`} />
        ) : (
          <span className="text-sm text-zinc-500 font-medium">{rank}</span>
        )}
      </div>
      <div className="flex-1">
        <p className={`text-sm font-medium ${isCurrentUser ? 'text-sky-300' : 'text-white'}`}>{name}</p>
      </div>
      <div className="text-right">
        <p className="text-lg font-light text-white">{value}</p>
        <p className="text-xs text-zinc-500">contratos</p>
      </div>
    </div>
  );
}

export default function MisResultadosPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await axios.get(`${API_URL}/api/comercial/mis-resultados`, { withCredentials: true });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setStats(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Un objetivo a 0 (sin configurar) no debe dar NaN/Infinity en los anillos
  const porcentaje = (valor, objetivo) => (objetivo ? Math.round((valor / objetivo) * 100) : 0);
  const progresoMes = stats ? porcentaje(stats.contratos_mes, stats.objetivo_mes) : 0;
  const progresoAno = stats ? porcentaje(stats.contratos_ano, stats.objetivo_ano) : 0;

  return (
    <ComercialLayout
      title="Mis Resultados"
      subtitle="Estadísticas personales"
      onLogout={handleLogout}
      headerActions={
        <Button
          variant="outline"
          onClick={fetchStats}
          disabled={loading}
          className="border-white/20 text-zinc-300 hover:text-white"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center" data-testid="mis-resultados-error">
          <p className="text-white font-medium">No se han podido cargar tus resultados</p>
          <p className="text-sm text-zinc-500 mt-1 mb-4">Comprueba tu conexión e inténtalo de nuevo.</p>
          <Button variant="outline" onClick={fetchStats} className="border-white/20 text-zinc-300 hover:text-white">
            <RefreshCw className="w-4 h-4 mr-2" />
            Reintentar
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* KPIs principales */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Contratos del mes"
              value={stats?.contratos_mes || 0}
              subtitle={`Objetivo: ${stats?.objetivo_mes || 0}`}
              icon={FileText}
              color="sky"
            />
            <StatCard
              title="Contratos del año"
              value={stats?.contratos_ano || 0}
              subtitle={`Objetivo: ${stats?.objetivo_ano || 0}`}
              icon={Calendar}
              color="emerald"
            />
            <StatCard
              title="Pruebas activas"
              value={stats?.pruebas_activas || 0}
              icon={Beaker}
              color="purple"
            />
            <StatCard
              title="Instalaciones pendientes"
              value={stats?.instalaciones_pendientes || 0}
              icon={Users}
              color="amber"
            />
          </div>

          {/* Objetivos y Ranking */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Objetivos */}
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <h3 className="text-lg font-light text-white font-['Outfit'] mb-6 flex items-center gap-2">
                <Target className="w-5 h-5 text-sky-400" />
                Objetivos
              </h3>
              
              <div className="flex items-center justify-around">
                <div className="text-center">
                  <ProgressRing progress={Math.min(progresoMes, 100)} color="sky" />
                  <p className="text-sm text-zinc-400 mt-3">Mensual</p>
                  <p className="text-xs text-zinc-500">{stats?.contratos_mes} / {stats?.objetivo_mes}</p>
                </div>
                <div className="text-center">
                  <ProgressRing progress={Math.min(progresoAno, 100)} color="emerald" />
                  <p className="text-sm text-zinc-400 mt-3">Anual</p>
                  <p className="text-xs text-zinc-500">{stats?.contratos_ano} / {stats?.objetivo_ano}</p>
                </div>
              </div>

              {/* Conversión */}
              <div className="mt-6 pt-6 border-t border-white/[0.06]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-zinc-400">Tasa de conversión</span>
                  <span className="text-lg font-light text-white">{stats?.conversion || 0}%</span>
                </div>
                <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${stats?.conversion || 0}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Ranking */}
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              <h3 className="text-lg font-light text-white font-['Outfit'] mb-6 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-400" />
                Ranking del Equipo
              </h3>
              
              <div className="space-y-2">
                {stats?.ranking?.map((item, index) => (
                  <RankingItem
                    key={index}
                    rank={index + 1}
                    name={item.nombre}
                    value={item.contratos}
                    isCurrentUser={index + 1 === stats?.posicion_ranking}
                  />
                ))}
              </div>

              {stats?.posicion_ranking && (
                <div className="mt-4 pt-4 border-t border-white/[0.06] text-center">
                  <p className="text-sm text-zinc-400">
                    Tu posición: <span className="text-sky-400 font-medium">#{stats.posicion_ranking}</span>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Info Odoo */}
          <div className="p-4 rounded-xl bg-amber-500/[0.08] border border-amber-500/20">
            <p className="text-sm text-amber-300/80 text-center font-['Manrope']">
              Los datos mostrados son preliminares. La integración completa con Odoo permitirá datos en tiempo real.
            </p>
          </div>
        </div>
      )}
    </ComercialLayout>
  );
}
